import { InfoCircleOutlined } from "@ant-design/icons";
import {
  integrationAccesses,
  integrationLabel,
  integrationRegistry,
} from "@p0security/shared/integrations/registry";
import {
  type AccessRequestOf,
  type Delegate,
  type DelegatesOf,
  type Output,
  type StagedAccessRequestOf,
  type StagedDelegatesOf,
  isAccessRequestOf,
} from "@p0security/shared/types/access/index";
import {
  type EmptyAccessDelegation,
  type EmptyAccesses,
  UNUSED_STAGED_ITEM,
} from "@p0security/shared/types/lifecycle";
import {
  PermissionRequest,
  type StagedPermissionRequest,
  isPermissionType,
} from "@p0security/shared/types/permission";
import type { SchemaAttribute } from "@p0security/shared/types/schema";
import { Flex, Typography } from "@p0security/ui";
import { isKeyOf } from "@p0security/util/types/is";
import { GraphTooltip } from "components/GraphTooltip";
import { AllIntegrationLogos } from "components/Integrations/IntegrationLogo";
import { capitalize, snakeCase } from "lodash";
import React, { useMemo } from "react";

import { Properties } from "../../Properties.tsx";

const UNDERSCORES = /_/g;

const autoLabel = (text: string) =>
  capitalize(snakeCase(text).replace(UNDERSCORES, " "));

/** The label/description trio shared by `Output` and `Parameter` descriptors
 * — derived from `Output` (rather than hand-written) so a rename of either
 * field in the shared type is a compile error here, not a silent runtime
 * no-op. A `Parameter` (whose `label` is required, not optional) structurally
 * satisfies this too. Deliberately excludes `hidden`: that field only ever
 * means "hidden in the CLI" (see `packages/core/src/lifecycle/commands.ts`),
 * and only for `Parameter` — `Output` doesn't have it at all, since the CLI
 * has no concept of hiding a generated output. This request detail page
 * shows every field regardless of CLI visibility. */
type FieldDescriptor = Pick<Output, "description" | "label">;

type DelegateDescriptor = Pick<
  Delegate<EmptyAccesses, string>,
  "accesses" | "resource"
>;

type DelegateEntry = (
  | DelegatesOf<EmptyAccessDelegation>
  | StagedDelegatesOf<EmptyAccessDelegation>
)[number];

/** Returns the descriptor a delegate was built from, given its parent's
 * static descriptor for its key (if any) and its stored `type` (if any). */
const getDelegateDescriptor = (
  staticDescriptor: DelegateDescriptor | undefined,
  type: string | undefined
): DelegateDescriptor | undefined =>
  // Matches the backend permissioner's `#typedDelegatee`: a delegate chosen at
  // request time (e.g. a bundle item) has no static descriptor on its parent,
  // so it resolves from its stored `type`. Delegates created before `type`
  // was stored only have the static descriptor.
  type && staticDescriptor?.resource !== type
    ? { accesses: integrationAccesses(type), resource: type }
    : staticDescriptor;

/** Builds a field's schema attribute from a `Parameter` or `Output`
 * descriptor.
 *
 * Both shapes carry the same optional `label`/`description` pair; this makes
 * `label` effectively required by falling back to an auto-generated one when
 * the descriptor doesn't set one. */
const attributeFrom = (
  descriptor: FieldDescriptor,
  fallbackLabel: string
): SchemaAttribute<string> => ({
  label: descriptor.label ?? autoLabel(fallbackLabel),
  help: descriptor.description,
});

/** One `Properties` table's worth of fields — the top-level request's own
 * fields, or one delegate's. */
type Section = {
  schema: Record<string, SchemaAttribute<string>>;
  value: Record<string, unknown>;
};

/** A delegate's fields, rendered as a separate titled table below the
 * request's own properties (`resource` is the delegate's integration key,
 * which selects its logo; `title` is that integration's label, e.g. "Google
 * Cloud"). */
type DelegateSection = Section & {
  key: string;
  resource: string;
  title: string;
};

function InnerAccessProperties<
  A extends EmptyAccesses,
  K extends keyof A & string,
>(props: {
  access: A[K];
  request: AccessRequestOf<A, K> | StagedAccessRequestOf<A, K>;
}): React.ReactNode {
  const { access, request } = props;

  const { delegates, schema, value } = useMemo(() => {
    // Each section accumulates its own schema/value pair. A field is always
    // listed (so its label shows), but a value equal to the
    // UNUSED_STAGED_ITEM sentinel is omitted from `value` — Properties.tsx
    // renders a missing value as "(No value)", same as a field that's simply
    // not staged yet.
    //
    // Both `access.parameters` and `access.generated` (and a delegate's own
    // schemas) are `Record<string, Parameter | Output>` descriptors sharing
    // the same label/description/hidden trio — `setFields` walks any schema
    // record, listing one field per key (so a field always shows, even if it
    // hasn't been staged/generated yet). `toValue` fetches the corresponding
    // staged value; `toAttribute` resolves the schema entry into a schema
    // attribute (falling back to `autoLabel` as each caller needs).
    const newSection = () => {
      const section: Section = { schema: {}, value: {} };
      const setFields = <T,>(
        namespace: string,
        fields: Record<string, T> | undefined,
        toValue: (key: string) => unknown,
        toAttribute: (key: string, descriptor: T) => SchemaAttribute<string>
      ) => {
        if (!fields) return;
        for (const [key, descriptor] of Object.entries(fields)) {
          const fieldValue = toValue(key);
          section.schema[`${namespace}.${key}`] = toAttribute(key, descriptor);
          if (fieldValue !== UNUSED_STAGED_ITEM)
            section.value[`${namespace}.${key}`] = fieldValue;
        }
      };
      return { section, setFields };
    };

    const topSection = newSection();
    topSection.setFields(
      "permission",
      access.parameters,
      (k) => request.permission[k],
      (k, descriptor) => attributeFrom(descriptor, k)
    );

    const generated = (
      "generated" in request ? request.generated : undefined
    ) as Record<string, unknown> | undefined;
    topSection.setFields(
      "generated",
      access.generated,
      (k) => generated?.[k],
      (k, descriptor) => attributeFrom(descriptor, k)
    );

    // A delegate's fields are driven by its own access's schema, same as the
    // two calls above — so a delegate with no matching schema (unregistered
    // key, or an access variant absent from its `accesses` map) contributes
    // no section at all, and one with a schema shows its fields even before
    // they've been staged (matching top-level `generated` fields). Each
    // delegate becomes its own titled section rather than rows merged into
    // the top-level table: a delegate is a separate grant P0 provisions in
    // the backend, and merging its fields in reads as duplication when it
    // shares fields with the request itself (e.g. gcloud's toolUser delegate
    // re-stages the request's own project/projectId). A nested delegate (one
    // in a delegate's own `request.delegation`) is likewise its own grant, so
    // it gets its own section, keyed by its full path (e.g. "parent.child")
    // since a `use` key is only unique among its siblings.
    const toDelegateSections = (
      entries: DelegateEntry[] | undefined,
      descriptors: Record<string, DelegateDescriptor | undefined> | undefined,
      parentKey?: string
    ): DelegateSection[] =>
      // Pre-migration requests may store `delegation` as a record, not an array
      Array.isArray(entries)
        ? entries.flatMap(({ key: use, request: delegateRequest }) => {
            // `resource` is the delegate's own integration key (e.g.
            // "aws-oidc"), and `accesses[delegateRequest.access]` is the
            // specific access the delegate staged, whose `generated`
            // descriptor is what actually typed `delegateRequest.generated`
            // (see StagedRequestDelegateOf) — so it's the authoritative
            // source for these fields' real labels.
            const descriptor = getDelegateDescriptor(
              descriptors?.[use],
              delegateRequest.type
            );
            const delegateAccess = descriptor?.accesses[delegateRequest.access];
            if (!descriptor || !delegateAccess) return [];

            const key = parentKey ? `${parentKey}.${use}` : use;
            // A delegate that isn't staged yet has no `generated` at all
            const generated: Record<string, unknown> | undefined =
              "generated" in delegateRequest
                ? delegateRequest.generated
                : undefined;
            const permission: Record<string, unknown> | undefined =
              delegateRequest.permission;
            const { section, setFields } = newSection();
            setFields(
              "generated",
              delegateAccess.generated,
              (k) => generated?.[k],
              (k, descriptor) => attributeFrom(descriptor, k)
            );
            setFields(
              "permission",
              delegateAccess.parameters,
              (k) => permission?.[k],
              (k, descriptor) => attributeFrom(descriptor, k)
            );
            const nested = toDelegateSections(
              delegateRequest.delegation,
              delegateAccess.delegation,
              key
            );
            return Object.keys(section.schema).length === 0
              ? nested
              : [
                  {
                    ...section,
                    key,
                    resource: descriptor.resource,
                    title: integrationLabel(descriptor.resource),
                  },
                  ...nested,
                ];
          })
        : [];

    const delegates = toDelegateSections(request.delegation, access.delegation);

    return { delegates, ...topSection.section };
  }, [access, request]);

  return (
    <Flex vertical gap={16}>
      <Properties inlineObjects labelKey="" value={value} schema={schema} />
      {delegates.map((d) => (
        <Flex key={d.key} vertical gap={8}>
          <Flex align="center" gap={8}>
            {AllIntegrationLogos[d.resource]}
            <Typography.Text strong>{d.title}</Typography.Text>
            <GraphTooltip title="Provisioned separately by P0 to support the requested access.">
              <InfoCircleOutlined className="cursor-help text-(--ant-color-text-secondary)" />
            </GraphTooltip>
          </Flex>
          <Properties
            inlineObjects
            labelKey=""
            value={d.value}
            schema={d.schema}
          />
        </Flex>
      ))}
    </Flex>
  );
}

export const AccessProperties: React.FC<{
  request: PermissionRequest | StagedPermissionRequest;
}> = (props) => {
  const { request } = props;

  if (!isPermissionType(request.type)) return null;
  if (!isAccessRequestOf(request.type, request)) return null;

  const registration = integrationRegistry[request.type];
  if (!registration) return null;

  const { accesses } = registration;

  if (!accesses) return null;
  const { access } = request;

  if (!isKeyOf(accesses, access)) return null;

  return (
    <InnerAccessProperties
      access={accesses[request.access]}
      request={request}
    />
  );
};
